import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {Avatar, Button, Detail, EMPHASIS, OutlinedPillVariant, Pill, PillVariant, Text} from '@forecast-it/design-system';
import type {WatchersList_ticket$key} from './__generated__/WatchersList_ticket.graphql';
import type {ticketingPerson_person$key} from '../../../graphql/fragments/__generated__/ticketingPerson_person.graphql';
import {ticketingPersonFragment, personDisplayName} from '../../../graphql/fragments/ticketingPerson';
import {useWatchTicket} from '../hooks/useWatchTicket';

const WatchersListFragment = graphql`
	fragment WatchersList_ticket on Ticket {
		id
		isWatching
		watcherCount
		assigneeId
		watchers {
			id
			person {
				id
				...ticketingPerson_person
			}
		}
	}
`;

interface Props {
	ticket: WatchersList_ticket$key;
	/** The viewer's person id, so their own row can be marked and the assignee's too. */
	currentPersonId?: string;
}

const Panel = styled.section`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	border-radius: ${p => p.theme.sys.size.borderradius['3']};
	background: ${p => p.theme.sys.color.background.neutral['1']};
	padding: ${p => p.theme.sys.spacing[400]};
`;
const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${p => p.theme.sys.spacing[300]};
`;
const TitleGroup = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const Title = styled.span`
	${p => p.theme.sys.font?.subheading?.['1']}
	color: ${p => p.theme.sys.color.text.content.main};
`;
const List = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const Row = styled.li`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const Who = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
	min-width: 0;
`;

function WatcherRow({
	person,
	isAssignee,
	isYou,
}: {
	person: ticketingPerson_person$key | null | undefined;
	isAssignee: boolean;
	isYou: boolean;
}) {
	const data = useFragment(ticketingPersonFragment, person ?? null);
	const name = data ? personDisplayName(data) : 'Unknown';
	return (
		<Row>
			<Who>
				<Avatar size="s" name={name} />
				<Text size="2">{isYou ? `${name} (you)` : name}</Text>
			</Who>
			{/* The assignee is watching because they own it, so there is nothing to
			    stop — the pill says why they are here instead. */}
			{isAssignee && (
				<Pill outlined outlinedVariant={OutlinedPillVariant.ACTIVE}>
					Assignee
				</Pill>
			)}
		</Row>
	);
}

/**
 * Who hears about this ticket, and whether you are one of them.
 *
 * The watch control is the only action here. The design also offers "Add watcher" and
 * a remove per row, but `watchTicket` / `unwatchTicket` take a ticket id and act on the
 * caller — there is no mutation for subscribing or removing someone else, so those two
 * affordances have no API behind them and are left out rather than rendered dead.
 */
export function WatchersList({ticket, currentPersonId}: Props) {
	const data = useFragment<WatchersList_ticket$key>(WatchersListFragment, ticket);
	const {watch, unwatch, inFlight} = useWatchTicket();

	const handleToggle = () => {
		if (data.isWatching) {
			unwatch(data.id);
		} else {
			watch(data.id);
		}
	};

	return (
		<Panel data-testid="watchers-panel">
			<Header>
				<TitleGroup>
					<Title>Watchers</Title>
					<Pill variant={PillVariant.NEUTRAL}>{String(data.watcherCount)}</Pill>
				</TitleGroup>
				<Button
					emphasis={data.isWatching ? EMPHASIS.MEDIUM : EMPHASIS.MONO}
					icon="watcher"
					disabled={inFlight}
					onPress={handleToggle}
					data-cy="watch-toggle"
				>
					{inFlight ? '…' : data.isWatching ? 'Following' : 'Watch'}
				</Button>
			</Header>

			<Detail color="medium">Watchers get notified when status, assignee, priority, or comments change.</Detail>

			{data.watchers.length > 0 && (
				<List>
					{data.watchers.map(w => (
						<WatcherRow
							key={w.id}
							person={w.person}
							isAssignee={Boolean(data.assigneeId && w.person?.id === data.assigneeId)}
							isYou={Boolean(currentPersonId && w.person?.id === currentPersonId)}
						/>
					))}
				</List>
			)}
		</Panel>
	);
}
